import VueRouter, { RouteConfig } from 'vue-router';
import accountRoutes from './account/account';
import accreditationRoutes from './accreditation';
import { app } from '@store/modules/app';
import appRedirectRoutes from './app-redirect';
import { augmentVueRouterPush } from './utils/augmented-push';
import checkoutRoutes from './checkout/checkout';
import educationRoutes from './education';
import emailUnsubscribeRoutes from './email';
import growthCalculatorRoutes from './growth-calculator';
import helpCenterRoutes from './help-center';
import homeRedirects from './redirects/home-redirects';
import { investmentEntity } from '@store/modules/investment-entity';
import investmentGoalRoutes from './investment-goals';
import investmentPlanningRoutes from './investment-planning';
import investmentsRedirects from './redirects/investments-redirects';
import iraAddFundsRoutes from './ira/ira-add-funds';
import iraCompleteOrderRoutes from './ira/ira-complete-order';
import iraCompleteRolloverRoutes from './ira/ira-complete-rollover';
import iraGeneralRoutes from './ira/ira-general';
import iraReacknowledgeRolloverRoutes from './ira/ira-reacknowledge-rollover';
import iraSignupRoutes from './ira/ira-signup';
import iraVerifyRoutes from './ira/ira-verify';
import legalRoutes from './legal';
import loginRoutes from './login/login';
import marketingRedirectRoutes from './marketing-redirects';
import microFeedbackRoutes from './micro-feedback';
import mobileRedirectRoutes from './mobile-redirect';
import modularLandingPageRoutes from './modular-landing-pages';
import noteInvestmentRoutes from './note-investment';
import offeringsRoutes from './offerings';
import { pageViewed } from '@utils/analytics';
import privateDocumentRedirectRoutes from './private-document-redirect';
import questionnaireRoutes from './questionnaire';
import redemptionRequestRoutes from './redemption-request';
import redirectRoutes from './redirects';
import referralRoutes from './referral';
import resetPasswordRoutes from './forgot-password/forgot-password';
import settingsRoutes from './account/settings';
import startInvestingRoutes from './start-investing';
import { user } from '@store/modules/user';
import Vue from 'vue';
import VueMeta from 'vue-meta';

augmentVueRouterPush();
Vue.use(VueRouter);
Vue.use(VueMeta);

/* eslint-disable @typescript-eslint/explicit-function-return-type */
const routes: RouteConfig[] = [
	{
		path: `/about`,
		name: 'about',
		meta: { minTokenScopeRequired: 'UNAUTHENTICATED' },
		component: () => import(/* webpackChunkName: "marketing-site-about" */ '@views/marketing-site/about.vue')
	},
	{
		path: `/press`,
		name: 'press',
		meta: { minTokenScopeRequired: 'UNAUTHENTICATED' },
		component: () => import(/* webpackChunkName: "marketing-site-press" */ '@views/marketing-site/press.vue')
	},
	{
		path: `/`,
		name: 'home',
		meta: { minTokenScopeRequired: 'UNAUTHENTICATED' },
		component: () => import(/* webpackChunkName: "marketing-site-home" */ '@views/marketing-site/home.vue')
	},
	{
		path: `/how-it-works`,
		name: 'how-it-works',
		meta: { minTokenScopeRequired: 'UNAUTHENTICATED' },
		component: () =>
			import(/* webpackChunkName: "marketing-site-how-it-works" */ '@views/marketing-site/how-it-works.vue')
	},
	{
		path: `/investments`,
		name: 'investments',
		meta: { minTokenScopeRequired: 'UNAUTHENTICATED' },
		component: () =>
			import(/* webpackChunkName: "marketing-site-investments" */ '@views/marketing-site/investments.vue')
	},
	{
		path: `/investments/core-plans/compare`,
		name: 'compare-core-plans',
		meta: { minTokenScopeRequired: 'UNAUTHENTICATED' },
		component: () =>
			import(
				/* webpackChunkName: "marketing-site-compare-core-plans" */ '@views/marketing-site/compare-core-plans.vue'
			)
	},
	{
		path: `/investments/advanced-plans/:planSlug`,
		props: true,
		name: 'plan-detail-pages',
		meta: { minTokenScopeRequired: 'UNAUTHENTICATED' },
		component: () =>
			import(/* webpackChunkName: "marketing-site-plan-detail-pages" */ '@views/marketing-site/advanced-plan.vue')
	},
	{
		path: `/jobs`,
		name: 'careers',
		meta: { minTokenScopeRequired: 'UNAUTHENTICATED' },
		component: () => import(/* webpackChunkName: "marketing-site-careers" */ '@views/marketing-site/careers.vue')
	},
	{
		path: `/assets`,
		alias: '/assets/:location',
		name: 'assets',
		meta: { minTokenScopeRequired: 'UNAUTHENTICATED' },
		component: () => import(/* webpackChunkName: "marketing-site-assets" */ '@views/marketing-site/assets.vue')
	},
	{
		path: `/referral-program/landing`,
		name: 'investor-referral-landing-redirect',
		meta: { minTokenScopeRequired: 'UNAUTHENTICATED' },
		redirect: { name: 'referral-landing' }
	},
	{
		path: `/invitation-program/landing`,
		name: 'referral-landing',
		meta: { minTokenScopeRequired: 'FULL_ACCESS' },
		beforeEnter: async (to, from, next) => {
			if (investmentEntity.userReferralEligibilityStatus === 'INELIGIBLE') {
				next({ name: 'account-overview' });
			} else {
				next();
			}
		},
		component: () => import(/* webpackChunkName: "referral-landing" */ '@views/referral/referral-landing.vue')
	},
	{
		path: `/nps`,
		name: 'thank-you-nps',
		meta: { minTokenScopeRequired: 'UNAUTHENTICATED' },
		component: () => import(/* webpackChunkName: "thank-you-nps" */ '@views/nps/nps-landing.vue')
	},
	{
		path: `/404`,
		name: '404',
		meta: { minTokenScopeRequired: 'UNAUTHENTICATED' },
		component: () => import(/* webpackChunkName: "404" */ '@views/error/not-found-404.vue')
	},
	{
		path: `/forbidden`,
		name: 'forbidden',
		meta: { minTokenScopeRequired: 'UNAUTHENTICATED' },
		component: () => import(/* webpackChunkName: "forbidden" */ '@views/error/forbidden.vue')
	},
	{
		path: `/guides/real-estate-investing-guide`,
		name: 'rei-guide',
		meta: { minTokenScopeRequired: 'UNAUTHENTICATED' },
		component: () =>
			import(/* webpackChunkName: "marketing-site-rei-guide" */ '@views/marketing-site/rei-guide.vue')
	},
	{
		path: `/why-private-real-estate`,
		name: 'why-private-real-estate',
		meta: { minTokenScopeRequired: 'UNAUTHENTICATED' },
		component: () =>
			import(
				/* webpackChunkName: "marketing-site-why-private-real-estate" */ '@views/marketing-site/why-private-real-estate.vue'
			)
	},
	{
		path: `/real-estate-assets/:reitElementId/view`,
		name: 'real-estate-assets',
		meta: { minTokenScopeRequired: 'UNAUTHENTICATED' },
		component: () =>
			import(
				/* webpackChunkName: "marketing-site-real-estate-assets" */ '@views/marketing-site/real-estate-assets.vue'
			)
	},
	{
		path: `/real-estate-strategies`,
		name: 'real-estate-strategies',
		meta: { minTokenScopeRequired: 'UNAUTHENTICATED' },
		component: () =>
			import(
				/* webpackChunkName: "marketing-site-real-estate-strategies" */ '@views/marketing-site/real-estate-strategies.vue'
			)
	},
	{
		path: `/strategy`,
		name: 'strategy',
		meta: { minTokenScopeRequired: 'UNAUTHENTICATED' },
		component: () =>
			import(
				/* webpackChunkName: "landing-page-strategy-overview" */ '@views/marketing-site/strategy-overview.vue'
			)
	},
	{
		path: `/ereits`,
		name: 'ereits',
		meta: { minTokenScopeRequired: 'UNAUTHENTICATED' },
		component: () => import(/* webpackChunkName: "marketing-site-ereits" */ '@views/marketing-site/ereits.vue')
	},
	{
		path: `/client-returns`,
		name: 'client-returns',
		meta: { minTokenScopeRequired: 'UNAUTHENTICATED' },
		component: () =>
			import(/* webpackChunkName: "marketing-site-client-returns" */ '@views/marketing-site/client-returns.vue')
	},
	{
		path: `/the-distance`,
		name: 'the-distance',
		meta: { minTokenScopeRequired: 'UNAUTHENTICATED' },
		component: () =>
			import(/* webpackChunkName: "newsletter-landing-page" */ '@views/newsletter/newsletter-landing-page.vue')
	},
	{
		path: `/reits/checkout/success`,
		name: 'reits-checkout-success',
		meta: { minTokenScopeRequired: 'FULL_ACCESS' },
		component: () => import(/* webpackChunkName: "reits-checkout-success" */ '@views/checkout/checkout-success.vue')
	},
	{
		path: `/signup/verify-signup-checkout`,
		name: 'verify-signup-checkout',
		meta: { minTokenScopeRequired: 'FULL_ACCESS' },
		component: () => import(/* webpackChunkName: "verify-signup-checkout" */ '@views/checkout/checkout-success.vue')
	},
	{
		path: `/welcome-video`,
		name: 'welcome-video',
		meta: { minTokenScopeRequired: 'UNAUTHENTICATED' },
		component: () => import(/* webpackChunkName: "welcome-video" */ '@views/account/welcome-video.vue')
	},
	{
		path: `/webview/home`,
		name: 'webview-home',
		meta: { minTokenScopeRequired: 'UNAUTHENTICATED' },
		component: () => import(/* webpackChunkName: "marketing-site-home-webview" */ '@views/webview/home.vue')
	},
	{
		path: `/webview/plan-details/:slug`,
		name: 'webview-plan-details',
		meta: { minTokenScopeRequired: 'UNAUTHENTICATED' },
		component: () => import(/* webpackChunkName: "plan-details-webview" */ '@views/webview/plan-details.vue')
	},
	{
		path: `/webview/service-level-details/:slug`,
		name: 'webview-service-level-details',
		meta: { minTokenScopeRequired: 'UNAUTHENTICATED' },
		component: () =>
			import(/* webpackChunkName: "service-level-details-webview" */ '@views/webview/service-level-details.vue')
	},
	{
		path: `/webview/track-record-annual-returns`,
		name: 'webview-track-record-annual-returns',
		meta: { minTokenScopeRequired: 'UNAUTHENTICATED' },
		component: () =>
			import(
				/* webpackChunkName: "webview-track-record-annual-returns" */ '@views/webview/track-record-annual-returns.vue'
			)
	},
	{
		path: `/webview/legal-disclosure`,
		name: 'webview-legal-disclosure',
		meta: { minTokenScopeRequired: 'UNAUTHENTICATED' },
		component: () =>
			import(/* webpackChunkName: "webview-legal-disclosure" */ '@views/webview/legal-disclosure.vue')
	},
	{
		path: `/webview/track-record-assumptions`,
		name: 'webview-track-record-assumptions',
		meta: { minTokenScopeRequired: 'UNAUTHENTICATED' },
		component: () =>
			import(
				/* webpackChunkName: "webview-track-record-assumptions" */ '@views/webview/track-record-assumptions.vue'
			)
	},
	{
		path: `/webview/premium-benefits`,
		name: 'webview-premium-benefits',
		meta: { minTokenScopeRequired: 'UNAUTHENTICATED' },
		component: () =>
			import(/* webpackChunkName: "webview-premium-benefits" */ '@views/webview/premium-benefits.vue')
	},
	{
		path: `/story/complete`,
		name: 'story-complete',
		meta: { minTokenScopeRequired: 'UNAUTHENTICATED' },
		component: () => import(/* webpackChunkName: "story-complete" */ '@views/stories/story-complete.vue')
	},
	{
		path: `/story/:id`,
		name: 'story-player',
		meta: { minTokenScopeRequired: 'UNAUTHENTICATED' },
		component: () => import(/* webpackChunkName: "story-player" */ '@views/stories/story-player.vue')
	},
	{
		path: `/user-research/confirm`,
		name: 'user-research-confirm',
		meta: { minTokenScopeRequired: 'FULL_ACCESS' },
		component: () =>
			import(/* webpackChunkName: "user-research-confirm" */ '@views/user-research/user-research-confirm.vue')
	},
	{
		path: '/investor-update/:investorUpdateId/view',
		name: 'investor-update',
		meta: { minTokenScopeRequired: 'UNAUTHENTICATED' },
		beforeEnter: async (to, from, next) => {
			const isId = /^\d+$/.test(to.params.investorUpdateId);
			if (isId) {
				next();
			} else {
				next({ name: '404' });
			}
		},
		component: () =>
			import(/* webpackChunkName: "investor-update-detail" */ '@views/investor-update/investor-update-detail.vue')
	},
	{
		path: `/get-iphone-app`,
		name: 'get-iphone-app',
		meta: { minTokenScopeRequired: 'UNAUTHENTICATED' },
		component: () => import(/* webpackChunkName: "get-iphone-app" */ '@views/mobile/get-iphone-app.vue')
	},
	{
		path: `/get-android-app`,
		name: 'get-android-app',
		meta: { minTokenScopeRequired: 'UNAUTHENTICATED' },
		component: () => import(/* webpackChunkName: "get-android-app" */ '@views/mobile/get-android-app.vue')
	},
	{
		path: `/login`,
		name: 'login',
		meta: { minTokenScopeRequired: 'UNAUTHENTICATED' },
		component: () => import(/* webpackChunkName: "login" */ '@views/login/login.vue')
	},
	{
		path: `/logout-success`,
		name: 'logout-success',
		meta: { minTokenScopeRequired: 'UNAUTHENTICATED' },
		component: () => import(/* webpackChunkName: "logout-logout-success" */ '@views/logout/logout-success.vue')
	},
	{
		path: `/contact`,
		name: 'contact',
		meta: { minTokenScopeRequired: 'UNAUTHENTICATED' },
		component: () => import(/* webpackChunkName: "contact" */ '@views/contact/contact.vue')
	},
	{
		path: '/data-visualizations/:dataVisualization',
		name: 'data-visualization',
		meta: { minTokenScopeRequired: 'UNAUTHENTICATED' },
		component: () =>
			import(/* webpackChunkName: "data-visualization" */ '@views/data-visualization/data-visualization.vue')
	},
	...accountRoutes,
	...settingsRoutes,
	accreditationRoutes,
	...questionnaireRoutes,
	startInvestingRoutes,
	...investmentGoalRoutes,
	iraSignupRoutes,
	iraAddFundsRoutes,
	iraCompleteOrderRoutes,
	iraCompleteRolloverRoutes,
	...iraGeneralRoutes,
	iraReacknowledgeRolloverRoutes,
	iraVerifyRoutes,
	...legalRoutes,
	...educationRoutes,
	...modularLandingPageRoutes,
	noteInvestmentRoutes,
	...checkoutRoutes,
	...emailUnsubscribeRoutes,
	...loginRoutes,
	...redemptionRequestRoutes,
	...referralRoutes,
	...resetPasswordRoutes,
	...marketingRedirectRoutes,
	...microFeedbackRoutes,
	...offeringsRoutes,
	...mobileRedirectRoutes,
	...appRedirectRoutes,
	...helpCenterRoutes,
	...privateDocumentRedirectRoutes,
	...growthCalculatorRoutes,
	...investmentPlanningRoutes,
	...redirectRoutes
];

const router = new VueRouter({
	mode: 'history',
	base: `${process.env.VUE_APP_BASE_PATH}`,
	scrollBehavior(to, from, savedPosition) {
		if (savedPosition) {
			return savedPosition;
		} else {
			if (to.hash) {
				return {
					selector: to.hash
				};
			}
			return { x: 0, y: 0 };
		}
	},
	routes
});
/* eslint-enable @typescript-eslint/explicit-function-return-type */

router.beforeEach(async (to, from, next): Promise<void> => {
	const redirectArray = [homeRedirects, investmentsRedirects];

	const foundRedirect = redirectArray.find((entry) => to.path.match(entry.regExPaths) !== null);

	if (foundRedirect) {
		next({ name: foundRedirect.routeName, replace: true });
	} else if (!to.matched.length) {
		next('/404');
	} else {
		const isAuthenticated =
			window.fundriseApp.oAuthToken !== '' && window.fundriseApp.requiresAdditionalAuthentication === false;

		app.SET_IS_AUTHENTICATED(isAuthenticated);
		app.UPDATE_ROUTE_TRANSITION({ to, from });

		if (isAuthenticated && !user.user) {
			await user.getUser();
		}
		next();
	}
});

router.beforeResolve((to, from, next) => {
	pageViewed(to, from);
	app.getCurrentBanner(to.path);
	next();
});

export default router;
